import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import {
  Box, Typography, TextField, Chip,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import DialogTemplate from 'components/shared/DialogTemplate';
import {
  ChipsInput,
} from 'components/FormInputs';
import { validatePatched } from 'logic/cottage';
import { useFormik } from 'components/shared/Formik';
import { tagsSelector } from 'selectors/cottages';

const EditLocation = ({
  isLoading,
  onSubmit,
  onClose,
  cottage,
}) => {
  const tags = useSelector(tagsSelector);
  const isSmall = useMediaQuery((theme) => theme.breakpoints.down('sm'));

  const intl = useIntl();
  const formik = useFormik({
    initialValues: {
      cities: [],
      ...cottage,
      tags: cottage ? cottage.tags : [],
    },
    enableReinitialize: true,
    validate: validatePatched({ intl, dialog: 'location' }),
    validateOnBlur: true,
    validateOnChange: false,
    onSubmit: async (values) => {
      await onSubmit(values);
    },
  });

  const {
    values, errors, handleSubmit,
    isSubmitting, dirty, submitForm, setFieldValue, handleBlur,
  } = formik;

  const removeTag = (v) => {
    setFieldValue('tags', values.tags.filter((t) => t !== v));
  };

  const tagsOptions = tags.map(({ id, name_sv, name_en }) => ({ value: id, label: intl.locale === 'en' ? name_en : name_sv }));

  return (
    <DialogTemplate
      title={(
        <>
          <FormattedMessage
            id="Location"
            defaultMessage="Location"
          />
          &nbsp;
          <>
            #{cottage?.id}
            {
              !isSmall && (
                <>{cottage?.title_sv ? <> - "{cottage?.title_sv}"</> : ''}</>
              )
            }
          </>
        </>
      )}
      isUpdating={isLoading || isSubmitting}
      pristine={!dirty}
      onClose={onClose}
      onSave={submitForm}
    >
      <Box pb={4}>
        <form onSubmit={handleSubmit} noValidate>
          <Box mb={3}>
            <ChipsInput
              label={intl.formatMessage({ id: 'location_tags', defaultMessage: 'Relevant cities' })}
              name="cities"
              onChange={(value) => setFieldValue('cities', values.cities.concat([value]))}
              onBlur={handleBlur}
              onDelete={(value) => setFieldValue('cities', values.cities.filter((v) => v !== value))}
              errors={errors}
              value={values.cities}
              required
            />
            <Box mt={errors.cities ? 2 : 0}>
              <Typography variant="body2" color="textSecondary">
                <FormattedMessage
                  id="cottages.citiesHelper"
                  defaultMessage="Fill in at least 1 city where you want your ad to be visible. Press enter or comma to add more than one"
                />
              </Typography>
            </Box>
          </Box>
          <Box mb={4}>
            <Autocomplete
              multiple
              name="tags"
              value={tagsOptions.filter((to) => !!values.tags && values.tags.includes(to.value))}
              id="facilities-multiselect"
              options={tagsOptions}
              getOptionLabel={(option) => option.label}
              getOptionSelected={(option) => values.tags.includes(option.value)}
              onChange={(e, selected) => {
                setFieldValue('tags', selected.map((s) => s.value));
              }}
              noOptionsText={intl.formatMessage({ id: 'No options match', defaultMessage: 'No options match' })}
              errors={errors}
              renderInput={(params) => {
                return (
                  <TextField
                    {...params}
                    variant="standard"
                    readOnly
                    inputProps={{
                      ...params.inputProps,
                      readOnly: true,
                    }}
                    label={(
                      <FormattedMessage
                        id="Tags"
                        defaultMessage="Location"
                      />
                    )}
                    placeholder={intl.formatMessage({ id: 'cottages.tagsHelp', defaultMessage: 'Click here to add location' })}
                  />
                );
              }}
              renderTags={(_values) => {
                return (
                  <Box my={0.5}>
                    {_values.map((v, i) => (
                      <Box mr={0.5} component="span" key={i}>
                        <Chip
                          style={{
                            marginBottom: 4,
                          }}
                          label={v.label}
                          color="primary"
                          size="small"
                          onDelete={() => removeTag(v.value)}
                        />
                      </Box>
                    ))}
                  </Box>
                );
              }}
            />
          </Box>
        </form>
      </Box>
    </DialogTemplate>
  );
};

EditLocation.propTypes = {
  isLoading: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  cottage: PropTypes.shape({
    id: PropTypes.number,
    title_sv: PropTypes.string,
    cities: PropTypes.arrayOf(PropTypes.string),
    tags: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
  }),
};

export default EditLocation;
